/**
 * Downloadable Resources API
 * Client-side functions for generating and downloading AI-created PDFs
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions(undefined, 'europe-west3');

/**
 * Generate a worksheet PDF for an activity
 * @param {Object} params
 * @param {string} params.activitySlug - The activity slug/ID
 * @param {number} [params.sessionNumber] - Session number (default: 1)
 * @param {string} [params.studentName] - Optional student name for personalization
 * @param {string} [params.communicationLevel] - pre-formal | semi-formal | formal
 * @param {string} [params.customInstructions] - Custom instructions for AI generation
 * @returns {Promise<{downloadUrl: string, storagePath: string, filename: string, fileSize: number, pageCount: number, generatedAt: string}>}
 */
export async function generateWorksheet(params) {
  const callable = httpsCallable(functions, 'generateWorksheetFunc');
  const result = await callable(params);
  return result.data;
}

/**
 * Generate a progress tracking sheet PDF
 * @param {Object} params
 * @param {string} [params.interventionBlockId] - Intervention block ID
 * @param {string} [params.activitySlug] - Activity slug (if not using intervention block)
 * @param {Array<{id: string, text: string}>} params.targets - List of targets to track
 * @param {string[]} params.studentNames - List of student names
 * @param {number} [params.weekCount] - Number of weeks to track (default: 6)
 * @param {string} [params.title] - Custom title for the sheet
 * @returns {Promise<{downloadUrl: string, storagePath: string, filename: string, fileSize: number, pageCount: number, generatedAt: string}>}
 */
export async function generateProgressSheet(params) {
  const callable = httpsCallable(functions, 'generateProgressSheetFunc');
  const result = await callable(params);
  return result.data;
}

/**
 * Generate a sensory story PDF
 * @param {Object} params
 * @param {string} params.theme - Story theme (e.g., "Going to the Beach", "Rainy Day")
 * @param {string[]} params.sensoryElements - Sensory elements to include (e.g., ["water", "sand", "sun"])
 * @param {string} [params.communicationLevel] - pre-formal | semi-formal | formal
 * @param {string} [params.format] - print | whiteboard
 * @param {string} [params.studentName] - Optional student name for personalization
 * @param {string} [params.customElements] - Additional custom elements
 * @returns {Promise<{downloadUrl: string, storagePath: string, filename: string, fileSize: number, pageCount: number, generatedAt: string}>}
 */
export async function generateSensoryStory(params) {
  const callable = httpsCallable(functions, 'generateSensoryStoryFunc');
  const result = await callable(params);
  return result.data;
}

/**
 * Generate visual support materials (comic strip conversations, social stories, visual schedules)
 * @param {Object} params
 * @param {string} params.supportType - comic_strip | social_story | visual_schedule
 * @param {string} params.scenario - Description of the scenario
 * @param {string} [params.studentContext] - Context about the student's needs
 * @param {number} [params.panelCount] - Number of panels for comic strips (default: 4)
 * @returns {Promise<{downloadUrl: string, storagePath: string, filename: string, fileSize: number, pageCount: number, generatedAt: string}>}
 */
export async function generateVisualSupport(params) {
  const callable = httpsCallable(functions, 'generateVisualSupportFunc');
  const result = await callable(params);
  return result.data;
}

/**
 * Download types available for different activity categories
 */
export const DOWNLOAD_TYPES = {
  // For intervention activities
  intervention: ['worksheet', 'progress_sheet'],
  
  // For sensory story activities
  sensory_story: ['story'],
  
  // For comic strip conversation activities
  comic_strip: ['visual_support'],
  
  // For social skills activities
  social_skills: ['visual_support', 'worksheet'],
};

/**
 * Get available download types for an activity based on its tags/type
 * @param {Object} activity - The activity object
 * @returns {string[]} Array of available download types
 */
export function getAvailableDownloads(activity) {
  const downloads = [];
  const tags = activity.tags || [];
  const title = (activity.title || '').toLowerCase();
  const activityType = activity.activityType;
  
  // Check for intervention activities
  if (activityType === 'structured' || tags.includes('intervention')) {
    downloads.push('worksheet');
  }
  
  // Check for sensory story activities
  if (title.includes('sensory story') || tags.includes('sensory-story')) {
    downloads.push('story');
  }
  
  // Check for comic strip conversation activities
  if (title.includes('comic strip') || tags.includes('comic-strip') || tags.includes('visual-support')) {
    downloads.push('visual_support');
  }
  
  // All structured activities can have progress sheets
  if (activityType === 'structured') {
    downloads.push('progress_sheet');
  }
  
  return downloads;
}

/**
 * Get display name for a download type
 * @param {string} type - Download type
 * @returns {string} Display name
 */
export function getDownloadTypeName(type) {
  const names = {
    worksheet: 'Worksheet',
    progress_sheet: 'Progress Sheet',
    story: 'Sensory Story',
    visual_support: 'Visual Support',
  };
  return names[type] || type;
}

/**
 * Get icon name for a download type (uses Icons.jsx icons)
 * @param {string} type - Download type
 * @returns {string} Icon name
 */
export function getDownloadTypeIcon(type) {
  const icons = {
    worksheet: 'ClipboardDocumentList',
    progress_sheet: 'ChartBar',
    story: 'BookOpen',
    visual_support: 'ChatBubble',
  };
  return icons[type] || 'Document';
}



